import * as THREE from 'three'

import { PlanarUVBuilder } from './planar-uv-builder'
import { UVBuilder } from './uv-builder'

const deg2rad = (degrees: number) => (degrees * Math.PI) / 180

const updateFrameUVs = (frameGeometry: THREE.Geometry) => {
  const builder = new UVBuilder(frameGeometry)

  let topY = 0
  const offsetTopYByBB = (b: PlanarUVBuilder) => {
    const maxY = b.getBB().max.y
    const minY = b.getBB().min.y
    const sizeY = maxY - minY
    topY = topY - sizeY
  }

  let bottomY = 0
  const offsetBottomYByBB = (b: PlanarUVBuilder) => {
    const maxY = b.getBB().max.y
    const minY = b.getBB().min.y
    const sizeY = maxY - minY
    bottomY = bottomY + sizeY
  }

  let leftX = 0
  const offsetLeftXByBB = (b: PlanarUVBuilder) => {
    const maxX = b.getBB().max.x
    const minX = b.getBB().min.x
    const sizeX = maxX - minX
    leftX = leftX + sizeX
  }

  let rightX = 0
  const offsetRightXByBB = (b: PlanarUVBuilder) => {
    const maxX = b.getBB().max.x
    const minX = b.getBB().min.x
    const sizeX = maxX - minX
    rightX = rightX - sizeX
  }

  const beamRotationPoint = new THREE.Vector2(0.5, 0.5)

  frameGeometry.faceVertexUvs = [
    [
      // outer top
      builder.planar(0, 5, 1, 'y').tap(offsetTopYByBB).toUV(), // B.lt (0) -> F.rt (5) -> B.rt (1)
      builder.planar(4, 5, 0, 'y').toUV(), // F.lt (4) -> F.rt (5) -> B.lt (0)
      // outer right
      builder
        .planar(5, 2, 1, 'x')
        .move(0, null, 'b')
        .tap(offsetRightXByBB)
        .rotate(deg2rad(90), beamRotationPoint)
        .toUV(), // F.rt (5) -> B.rb (2) -> B.rt (1)
      builder
        .planar(5, 6, 2, 'x')
        .move(0, null, 'c')
        .rotate(deg2rad(90), beamRotationPoint)
        .toUV(), // F.rt (5) -> F.rb (6) -> B.rb (2)
      // outer bottom
      builder
        .planar(3, 2, 6, 'y')
        .move(null, 0, 'c')
        .tap(offsetBottomYByBB)
        .toUV(), // B.lb (3) -> B.rb (2) -> F.rb (6)
      builder.planar(3, 6, 7, 'y').move(null, 0, 'b').toUV(), // B.lb (3) -> F.rb (6) -> F.lb (7)
      // outer left
      builder
        .planar(0, 3, 4, 'x')
        .move(1, null, 'c')
        .tap(offsetLeftXByBB)
        .rotate(deg2rad(90), beamRotationPoint)
        .toUV(), // B.lt (0) -> B.lb (3) -> F.lt (4)
      builder
        .planar(4, 3, 7, 'x')
        .move(1, null, 'a')
        .rotate(deg2rad(90), beamRotationPoint)
        .toUV(), // F.lt (4) -> B.lb (3) -> F.lb (7)
      // top border
      builder.planar(4, 9, 5, 'z').offset(0, topY).toUV(), // F.lt (4) -> I.rt (9) -> F.rt (5)
      builder.planar(4, 8, 9, 'z').offset(0, topY).tap(offsetTopYByBB).toUV(), // F.lt (4) -> I.lt (8) -> I.rt (9)
      // right border
      builder
        .planar(5, 10, 6, 'z')
        .move(0, null, 'b')
        .offset(-rightX, 0)
        .rotate(deg2rad(90), beamRotationPoint)
        .toUV(), // F.rt (5) -> I.rb (10) -> F.rb (6)
      builder
        .planar(5, 9, 10, 'z')
        .move(0, null, 'c')
        .offset(-rightX, 0)
        .tap(offsetRightXByBB)
        .rotate(deg2rad(90), beamRotationPoint)
        .toUV(), // F.rt (5) -> I.rt (9) -> I.rb (10)
      // bottom border
      builder.planar(10, 7, 6, 'z').offset(0, bottomY).toUV(), // I.rb (10) -> F.lb (7) -> F.rb (6)
      builder
        .planar(10, 11, 7, 'z')
        .offset(0, bottomY)
        .tap(offsetBottomYByBB)
        .toUV(), // I.rb (10) -> I.lb (11) -> F.lb (7)
      // left border
      builder
        .planar(4, 11, 8, 'z')
        .move(1, null, 'c')
        .offset(-leftX, 0)
        .rotate(deg2rad(90), beamRotationPoint)
        .toUV(), // F.lt (4) -> I.lb (11) -> I.lt (8)
      builder
        .planar(4, 7, 11, 'z')
        .move(1, null, 'c')
        .offset(-leftX, 0)
        .tap(offsetLeftXByBB)
        .rotate(deg2rad(90), beamRotationPoint)
        .toUV(), // F.lt (4) -> F.lb (7) -> I.lb (11)
      // top inset
      builder.planar(8, 13, 9, 'y').move(null, 1, 'b').offset(0, topY).toUV(), // I.lt (8) -> A.rt (13) [1] -> I.rt (9)
      builder
        .planar(8, 12, 13, 'y')
        .move(null, 1, 'c')
        .offset(0, topY)
        .tap(offsetTopYByBB)
        .toUV(), // I.lt (8) -> A.lt (12) [0] -> A.rt (13) [1]
      // right inset
      builder
        .planar(9, 14, 10, 'x')
        .move(0, null, 'b')
        .offset(-rightX, 0)
        .rotate(deg2rad(90), beamRotationPoint)
        .toUV(), // I.rt (9) -> A.rb (14) [2] -> I.rb (10)
      builder
        .planar(9, 13, 14, 'x')
        .move(0, null, 'c')
        .offset(-rightX, 0)
        .rotate(deg2rad(90), beamRotationPoint)
        .toUV(), // I.rt (9) -> A.rt (13) [1] -> A.rb (14) [2]
      // bottom inset
      builder
        .planar(10, 15, 11, 'y')
        .move(null, 0, 'a')
        .offset(0, bottomY)
        .toUV(), // I.rb (10) -> A.lb (15) [3] -> I.lb (11)
      builder
        .planar(10, 14, 15, 'y')
        .move(null, 0, 'a')
        .offset(0, bottomY)
        .toUV(), // I.rb (10) -> A.rb (14) [2] -> A.lb (15) [3]
      // left inset
      builder
        .planar(11, 15, 12, 'x')
        .move(1, null, 'a')
        .offset(-leftX, 0)
        .rotate(deg2rad(90), beamRotationPoint)
        .toUV(), // I.lb (11) -> A.lb (15) [3] -> A.lt (12) [0]
      builder
        .planar(11, 12, 8, 'x')
        .move(1, null, 'a')
        .offset(-leftX, 0)
        .rotate(deg2rad(90), beamRotationPoint)
        .toUV(), // I.lb (11) -> A.lt (12) [0] -> I.lt (8)
    ],
  ]

  frameGeometry.uvsNeedUpdate = true
  frameGeometry.elementsNeedUpdate = true
}

const updatePassepartoutUVs = (passepartoutGeometry: THREE.Geometry) => {
  const builder = new UVBuilder(passepartoutGeometry)

  const facePlanars = passepartoutGeometry.faces.map((face) => {
    return builder.planar(face.a, face.b, face.c, 'z').toUV()
  })

  passepartoutGeometry.faceVertexUvs = [[...facePlanars]]

  passepartoutGeometry.uvsNeedUpdate = true
  passepartoutGeometry.elementsNeedUpdate = true
}

interface MainFrameSettings {
  artWidth: number
  artHeight: number
  borderWidth: number
  passepartoutWidth: number
  depth: number
  inset: number
}

interface BackFaceFrameSettings {
  generateBackFace: boolean
}

type FullFrameSettings = MainFrameSettings & BackFaceFrameSettings

export type PartialMainFrameSettings = Partial<MainFrameSettings>
export type PartialFullFrameSettings = Partial<FullFrameSettings>

export const DEFAULT_FRAME_SETTINGS: FullFrameSettings = {
  artWidth: 2,
  artHeight: 1,
  borderWidth: 0.2,
  passepartoutWidth: 0,
  depth: 0.3,
  inset: 0.2,
  generateBackFace: false,
}

const faceArrayToFace = (fv: number[]) => new THREE.Face3(fv[0], fv[1], fv[2])

const generateFrameVertices = (settings: MainFrameSettings) => {
  const ahw = settings.artWidth / 2
  const ahh = settings.artHeight / 2

  const b = settings.borderWidth
  const p = settings.passepartoutWidth
  const d = settings.depth
  const i = settings.inset

  // Vertices, positioned by watching from front.
  // All vertices are laid out in quad fashion, following CSS TRBL order.
  // Names in format of <SET>.<xy>.
  return [
    // BACK (art + border + passepartout)
    [-ahw - b - p, ahh + b + p, 0], // 0, BACK, left top (B.lt)
    [ahw + b + p, ahh + b + p, 0], // 1, BACK, right top (B.rt)
    [ahw + b + p, -ahh - b - p, 0], // 2, BACK, right bottom (B.rb)
    [-ahw - b - p, -ahh - b - p, 0], // 3, BACK, left bottom (B.lb)
    // FRONT (art + border + passepartout)
    [-ahw - b - p, ahh + b + p, d], // 4, FRONT, left top (F.lt)
    [ahw + b + p, ahh + b + p, d], // 5, FRONT, right top (F.rt)
    [ahw + b + p, -ahh - b - p, d], // 6, FRONT, right bottom (F.rb)
    [-ahw - b - p, -ahh - b - p, d], // 7, FRONT, left bottom (F.lb)
    // INSET (art + passepartout)
    [-ahw - p, ahh + p, d], // 8, INSET, left top (I.lt)
    [ahw + p, ahh + p, d], // 9, INSET, right top (I.rt)
    [ahw + p, -ahh - p, d], // 10, INSET, right bottom (I.rb)
    [-ahw - p, -ahh - p, d], // 11, INSET, left bottom (I.lb)
    // PASSEPARTOUT (art + passepartout)
    [-ahw - p, ahh + p, d - i], // 12, PASSEPARTOUT, left top (P.lt)
    [ahw + p, ahh + p, d - i], // 13, PASSEPARTOUT, right top (P.rt)
    [ahw + p, -ahh - p, d - i], // 14, PASSEPARTOUT, right bottom (P.rb)
    [-ahw - p, -ahh - p, d - i], // 15, PASSEPARTOUT, left bottom (P.lb)
    // ART (art)
    [-ahw, ahh, d - i], // 16, ART, left top (A.lt)
    [ahw, ahh, d - i], // 17, ART, right top (A.rt)
    [ahw, -ahh, d - i], // 18, ART, right bottom (A.rb)
    [-ahw, -ahh, d - i], // 19, ART, left bottom (A.lb)
  ].map((v) => new THREE.Vector3(...v))
}

export const generateFrame = (
  frameGeometry: THREE.Geometry,
  passepartoutGeometry: THREE.Geometry,
  artGeometry: THREE.Geometry,
  settings: PartialFullFrameSettings = DEFAULT_FRAME_SETTINGS
) => {
  const normalizedSettings = Object.assign({}, DEFAULT_FRAME_SETTINGS, settings)
  const { artWidth, artHeight, generateBackFace } = normalizedSettings

  const vertices = generateFrameVertices(normalizedSettings)

  //
  // Setup frame geometry.
  //

  frameGeometry.vertices.push(...vertices)

  // TODO: Beam oriented layout.
  // TODO: Name triangles.
  // Frame faces, positioned by face rings.
  // All faces are laid out in ring fashion, following CSS TRBL order.
  const frameFaces = [
    // outer top
    [0, 5, 1], // B.lt (0) -> F.rt (5) -> B.rt (1)
    [4, 5, 0], // F.lt (4) -> F.rt (5) -> B.lt (0)
    // outer right
    [5, 2, 1], // F.rt (5) -> B.rb (2) -> B.rt (1)
    [5, 6, 2], // F.rt (5) -> F.rb (6) -> B.rb (2)
    // outer bottom
    [3, 2, 6], // B.lb (3) -> B.rb (2) -> F.rb (6)
    [3, 6, 7], // B.lb (3) -> F.rb (6) -> F.lb (7)
    // outer left
    [0, 3, 4], // B.lt (0) -> B.lb (3) -> F.lt (4)
    [4, 3, 7], // F.lt (4) -> B.lb (3) -> F.lb (7)
    // top border
    [4, 9, 5], // F.lt (4) -> I.rt (9) -> F.rt (5)
    [4, 8, 9], // F.lt (4) -> I.lt (8) -> I.rt (9)
    // right border
    [5, 10, 6], // F.rt (5) -> I.rb (10) -> F.rb (6)
    [5, 9, 10], // F.rt (5) -> I.rt (9) -> I.rb (10)
    // bottom border
    [10, 7, 6], // I.rb (10) -> F.lb (7) -> F.rb (6)
    [10, 11, 7], // I.rb (10) -> I.lb (11) -> F.lb (7)
    // left border
    [4, 11, 8], // F.lt (4) -> I.lb (11) -> I.lt (8)
    [4, 7, 11], // F.lt (4) -> F.lb (7) -> I.lb (11)
    // top inset
    [8, 13, 9], // I.lt (8) -> P.rt (13) [1] -> I.rt (9)
    [8, 12, 13], // I.lt (8) -> P.lt (12) [0] -> P.rt (13) [1]
    // right inset
    [9, 14, 10], // I.rt (9) -> P.rb (14) [2] -> I.rb (10)
    [9, 13, 14], // I.rt (9) -> P.rt (13) [1] -> P.rb (14) [2]
    // bottom inset
    [10, 15, 11], // I.rb (10) -> P.lb (15) [3] -> I.lb (11)
    [10, 14, 15], // I.rb (10) -> P.rb (14) [2] -> P.lb (15) [3]
    // left inset
    [11, 15, 12], // I.lb (11) -> P.lb (15) [3] -> P.lt (12) [0]
    [11, 12, 8], // I.lb (11) -> P.lt (12) [0] -> I.lt (8)
  ].map(faceArrayToFace)

  // Add Back Face, if requested.
  if (generateBackFace) {
    const backFaceFaces = [
      [0, 1, 2],
      [3, 0, 2],
    ].map(faceArrayToFace)

    frameFaces.unshift(...backFaceFaces)
  }

  frameGeometry.faces.push(...frameFaces)

  frameGeometry.computeFaceNormals()
  frameGeometry.computeBoundingBox()

  updateFrameUVs(frameGeometry)

  //
  // Setup passepartout geometry.
  //

  passepartoutGeometry.vertices.push(
    ...[12, 13, 14, 15, 16, 17, 18, 19].map(
      (vertexIndex) => vertices[vertexIndex]
    )
  )

  const passepartoutFaces = [
    // top
    [0, 4, 1], // P.lt (12) -> A.lt (16) -> P.rt (13) (top triangle)
    [1, 4, 5], // P.rt (13) -> A.lt (16) -> A.rt (17) (bottom triangle)
    // right
    [1, 5, 2], // P.rt (13) -> A.rt (17) -> P.rb (14) (right triangle)
    [2, 5, 6], // P.rb (14) -> A.rt (17) -> A.rb (18) (left triangle)
    // bottom
    [2, 6, 3], // P.rb (14) -> A.rb (18) -> P.lb (15) (bottom triangle)
    [3, 6, 7], // P.lb (15) -> A.rb (18) -> A.lb (19) (top triangle)
    // left
    [3, 7, 0], // P.lb (15) -> A.lb (19) -> P.lt (12) (left triangle)
    [0, 7, 4], // P.lt (12) -> A.lb (19) -> A.lt (16) (right triangle)
  ].map(faceArrayToFace)

  passepartoutGeometry.faces.push(...passepartoutFaces)

  passepartoutGeometry.computeFaceNormals()
  passepartoutGeometry.computeBoundingBox()

  //
  // Setup passepartout UV's.
  //

  updatePassepartoutUVs(passepartoutGeometry)

  //
  // Setup preview geometry.
  //

  artGeometry.vertices.push(
    ...[16, 17, 18, 19].map((vertexIndex) => vertices[vertexIndex])
  )

  artGeometry.verticesNeedUpdate = true

  const artFaces = [
    [0, 2, 1], // A.lt (16) -> A.rb (18) -> A.rt (17) (right triangle)
    [0, 3, 2], // A.lt (16) -> A.lb (19) -> A.rb (18) (left triangle)
  ].map(faceArrayToFace)

  artGeometry.faces.push(...artFaces)

  artGeometry.computeFaceNormals()

  //
  // Setup art UV's.
  //

  const aspectRatio = artWidth / artHeight
  const adjustedWidth = 1 * aspectRatio
  const adjustedHeight = 1 / aspectRatio

  let vt = 1,
    hr = 1,
    vb = 0,
    hl = 0

  // Crop horizontal.
  if (aspectRatio < 1) {
    const stretchDifference = adjustedWidth - 1
    const stretchWidth = stretchDifference / 2

    hr = 1 + stretchWidth
    hl = 0 - stretchWidth
    // Crop vertical.
  } else if (aspectRatio > 1) {
    const stretchDifference = adjustedHeight - 1
    const stretchHeight = stretchDifference / 2

    vt = 1 + stretchHeight
    vb = 0 - stretchHeight
  }

  artGeometry.faceVertexUvs = [
    [
      [
        new THREE.Vector2(hl, vt),
        new THREE.Vector2(hr, vb),
        new THREE.Vector2(hr, vt),
      ],
      [
        new THREE.Vector2(hl, vt),
        new THREE.Vector2(hl, vb),
        new THREE.Vector2(hr, vb),
      ],
    ],
  ]

  artGeometry.uvsNeedUpdate = true
  artGeometry.elementsNeedUpdate = true
}

export const updateFrame = (
  frameGeometry: THREE.Geometry,
  passepartoutGeometry: THREE.Geometry,
  artGeometry: THREE.Geometry,
  settings: PartialMainFrameSettings = DEFAULT_FRAME_SETTINGS
) => {
  const normalizedSettings = Object.assign({}, DEFAULT_FRAME_SETTINGS, settings)
  const { artWidth, artHeight } = normalizedSettings

  const vertices = generateFrameVertices(normalizedSettings)

  //
  // Update frame geometry.
  //

  vertices.forEach((v, i) => {
    frameGeometry.vertices[i].set(v.x, v.y, v.z)
  })

  frameGeometry.verticesNeedUpdate = true
  frameGeometry.elementsNeedUpdate = true
  frameGeometry.computeFaceNormals()
  frameGeometry.computeBoundingBox()

  updateFrameUVs(frameGeometry)

  //
  // Update passepartout geometry.
  //
  ;[12, 13, 14, 15, 16, 17, 18, 19]
    .map((vertexIndex) => vertices[vertexIndex])
    .forEach((v, i) => {
      passepartoutGeometry.vertices[i].set(v.x, v.y, v.z)
    })

  passepartoutGeometry.verticesNeedUpdate = true
  passepartoutGeometry.elementsNeedUpdate = true
  passepartoutGeometry.computeFaceNormals()
  passepartoutGeometry.computeBoundingBox()

  //
  // Update passepartout UV's.
  //

  updatePassepartoutUVs(passepartoutGeometry)

  //
  // Update art geometry.
  //
  ;[16, 17, 18, 19]
    .map((vertexIndex) => vertices[vertexIndex])
    .forEach((v, i) => {
      artGeometry.vertices[i].set(v.x, v.y, v.z)
    })

  artGeometry.verticesNeedUpdate = true
  artGeometry.elementsNeedUpdate = true
  artGeometry.computeFaceNormals()

  //
  // Update art UV's.
  //

  const aspectRatio = artWidth / artHeight
  const adjustedWidth = 1 * aspectRatio
  const adjustedHeight = 1 / aspectRatio

  let vt = 1,
    hr = 1,
    vb = 0,
    hl = 0

  // Crop horizontal.
  if (aspectRatio < 1) {
    const stretchDifference = adjustedWidth - 1
    const stretchWidth = stretchDifference / 2

    hr = 1 + stretchWidth
    hl = 0 - stretchWidth
    // Crop vertical.
  } else if (aspectRatio > 1) {
    const stretchDifference = adjustedHeight - 1
    const stretchHeight = stretchDifference / 2

    vt = 1 + stretchHeight
    vb = 0 - stretchHeight
  }

  artGeometry.faceVertexUvs[0][0][0].set(hl, vt)
  artGeometry.faceVertexUvs[0][0][1].set(hr, vb)
  artGeometry.faceVertexUvs[0][0][2].set(hr, vt)

  artGeometry.faceVertexUvs[0][1][0].set(hl, vt)
  artGeometry.faceVertexUvs[0][1][1].set(hl, vb)
  artGeometry.faceVertexUvs[0][1][2].set(hr, vb)

  artGeometry.uvsNeedUpdate = true
}
